import {
  Component,
  ChangeDetectionStrategy,
  input,
  computed,
  inject,
  signal,
  effect,
  untracked,
  DestroyRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';

import { Ticket, DescriptionSection, SocAction, EnrichedSocAction } from '../../models/ticket.models';
import { CasesService } from '../../services/cases.service';
import { ResponseActionsService } from '../../services/response-actions.service';
import { parseDescription } from '../../utils/description-parser';
import { parseSocActions } from '../../utils/soc-action-parser';
import { extractCaseOrigins, noOriginReason } from '../../utils/case-origin';
import { CaseBlastRadiusService, CaseReach, ResolvedOrigin } from '../../services/case-blast-radius.service';
import { CaseBlastRadiusComponent } from '../case-blast-radius/case-blast-radius.component';
import { intentQueryParams, PivotIntent } from '../../../inventory/pivot/models/pivot-intents';
import { PivotStateService } from '../../../inventory/pivot/services/pivot-state.service';
import { matchActionDefinition } from '../../utils/action-type-map';
import {
  getPriorityColor,
  getPriorityBadgeClass,
  getStatusBadgeClass,
  shortenStatusLabel,
  getSlaBadgeClass,
  getSlaColor,
  formatRelativeTime,
} from '../../utils/ticket-helpers';

import { CaseDetailSectionComponent, DetailRow } from './components/case-detail-section/case-detail-section.component';
import { ParsedDescriptionComponent } from './components/parsed-description/parsed-description.component';
import { CaseSocButtonComponent } from '../case-soc-button/case-soc-button.component';

@Component({
  selector: 'app-case-detail-panel',
  standalone: true,
  imports: [
    CommonModule,
    ButtonsModule,
    DialogsModule,
    CaseDetailSectionComponent,
    ParsedDescriptionComponent,
    CaseSocButtonComponent,
    CaseBlastRadiusComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './case-detail-panel.component.html',
  styleUrls: ['./case-detail-panel.component.scss'],
})
export class CaseDetailPanelComponent {
  ticket = input.required<Ticket>();
  casesService = inject(CasesService);
  responseActionsService = inject(ResponseActionsService);

  parsedDescription = computed(() => parseDescription(this.ticket().description));

  priorityColor = computed(() => getPriorityColor(this.ticket().priority));
  priorityBadge = computed(() => getPriorityBadgeClass(this.ticket().priority));
  statusBadge = computed(() => getStatusBadgeClass(this.ticket().ticket_status));
  statusLabel = computed(() => shortenStatusLabel(this.ticket().ticket_status));
  slaBadge = computed(() => getSlaBadgeClass(this.ticket().sla_violation_type));
  slaColor = computed(() => getSlaColor(this.ticket().sla_violation_type));
  relativeTime = computed(() => formatRelativeTime(this.ticket().created_time));

  // Extract the executive summary section (shown prominently)
  executiveSummary = computed(() => {
    const parsed = this.parsedDescription();
    const section = parsed.sections.find((s) => s.title === 'Executive Summary');
    return section?.content ?? parsed.summary ?? '';
  });

  // ── Blast radius (ONE-3516) ───────────────────────────────────────────────

  private destroyRef = inject(DestroyRef);
  private blastService = inject(CaseBlastRadiusService);
  private pivotState = inject(PivotStateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  public readonly resolvedOrigins = signal<ResolvedOrigin[]>([]);
  public readonly blastLoading = signal<boolean>(false);
  /** What the root touches, drawn as the map itself. */
  public readonly blastReach = signal<CaseReach | null>(null);
  public readonly reachLoading = signal<boolean>(false);

  /** Why there is no blast radius, when there is none. */
  public readonly blastEmptyReason = computed(() => noOriginReason(this.ticket()));

  constructor() {
    /**
     * Resolve the case's assets whenever the selected case changes.
     *
     * Two stages on purpose: the root appears as soon as names are matched, and
     * what it touches fills in after. Waiting for the neighbourhood would hold
     * the whole panel on the slowest of several independent calls.
     */
    effect(() => {
      const ticket = this.ticket();
      // SOC action rows carry the acted-on account for vendors that put it
      // nowhere else; they are already parsed for the actions list.
      const origins = extractCaseOrigins(ticket, this.socResponseActions());

      untracked(() => {
        this.resolvedOrigins.set([]);
        this.blastReach.set(null);
        if (!origins.length) {
          this.blastLoading.set(false);
          this.reachLoading.set(false);
          return;
        }
        this.blastLoading.set(true);
        this.blastService
          .resolve(origins)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((resolved) => {
            this.resolvedOrigins.set(resolved);
            this.blastLoading.set(false);

            // The root's neighbourhood IS the map.
            const root = resolved[0];
            if (root?.entityId !== null && root !== undefined) {
              this.reachLoading.set(true);
              this.blastService
                .reachOf(root, this.pivotState.scale())
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((reach) => {
                  this.blastReach.set(reach);
                  this.reachLoading.set(false);
                });
            }

            // The OTHER named assets get a count beside their name, not a map.
            const others = resolved.slice(1);
            if (others.length) {
              this.blastService
                .withDegrees(resolved, this.pivotState.scale())
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((withDegrees) => this.resolvedOrigins.set(withDegrees));
            }
          });
      });
    });
  }

  /**
   * Open the Visualizer already answering the chosen question.
   *
   * `return` carries the case URL so the analyst comes back to the case they
   * left, not to the case list — the same contract ONE-3504 established for
   * Asset Intelligence.
   */
  public onExplore(event: { resolved: ResolvedOrigin; intent: PivotIntent }): void {
    const { resolved, intent } = event;
    if (resolved.entityId === null) return;

    const returnUrl = this.router.url;
    this.router.navigate(['/customer/asset-intelligence/pivot'], {
      queryParams: {
        ...intentQueryParams(
          resolved.origin.kind,
          resolved.entityId,
          resolved.resolvedName ?? resolved.origin.name,
          intent,
          returnUrl
        ),
        organizationId: this.route.snapshot.queryParams['organizationId'],
      },
    });
  }

  // Extract and parse SOC Response Actions into structured items
  private socResponseParse = computed(() => {
    const parsed = this.parsedDescription();
    const section = parsed.sections.find((s) => s.title === 'SOC Response Actions');
    if (!section) return { actions: [] as SocAction[], narrative: null };
    return parseSocActions(section.content);
  });

  socResponseActions = computed<SocAction[]>(() => this.socResponseParse().actions);

  /**
   * Section text that produced no actions, shown as prose.
   *
   * Without this the section would simply render empty, which reads as "the SOC
   * took no action" — a claim about the case rather than about the parser.
   */
  socResponseNarrative = computed<string | null>(() => this.socResponseParse().narrative);

  // Enriched actions: parsed SOC actions + matched available actions + execution state
  enrichedActions = computed<EnrichedSocAction[]>(() => {
    const actions = this.socResponseActions();
    const available = this.ticket().available_actions ?? [];
    const ticketId = this.ticket().request_id;
    // Read the signal to trigger reactivity on state changes
    this.responseActionsService.executionStates();

    return actions.map((action) => {
      const matched = matchActionDefinition(action.label, available);
      const execState = this.responseActionsService.getExecutionState(ticketId, action.label);
      return {
        ...action,
        actionType: matched?.action_type ?? null,
        availableAction: matched,
        executionState: execState,
      };
    });
  });

  // Recommended Remediation from parsed description
  remediation = computed(() => {
    const parsed = this.parsedDescription();
    return (
      parsed.sections.find(
        (s) =>
          s.title === 'Recommended Remediation' ||
          s.title === 'Remediation Actions' ||
          s.title === 'SOC Recommended Actions'
      ) ?? null
    );
  });

  // All remaining sections (not summary, not SOC actions, not remediation)
  additionalSections = computed<DescriptionSection[]>(() => {
    const parsed = this.parsedDescription();
    const promoted = new Set([
      'Executive Summary',
      'SOC Response Actions',
      'Recommended Remediation',
      'Remediation Actions',
      'SOC Recommended Actions',
    ]);
    return parsed.sections.filter((s) => !promoted.has(s.title));
  });

  // Detail rows for the sidebar
  detailRows = computed<DetailRow[]>(() => {
    const t = this.ticket();
    return [
      { label: 'Issue Type', value: t.issue_type },
      { label: 'Source', value: t.ticket_source },
      { label: 'Created', value: t.created_time ? new Date(t.created_time).toLocaleString() : undefined },
      { label: 'Closed', value: t.ticket_closed_time ? new Date(t.ticket_closed_time).toLocaleString() : undefined },
      {
        label: 'Last Response',
        value: t.customer_response_time ? new Date(t.customer_response_time).toLocaleString() : undefined,
      },
      { label: 'Source IP', value: t.source_ip_address_hostname_mac ?? undefined },
      { label: 'Destination IP', value: t.destination_ip_address_hostname_mac ?? undefined },
      { label: 'Customer Status', value: t.customer_defined_status ?? undefined },
    ];
  });

  formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString();
  }

  objectKeys = Object.keys;

  // --- Action handlers ---

  onTakeAction(action: EnrichedSocAction): void {
    const ticketId = this.ticket().request_id;
    this.responseActionsService.executionStates.update((map) => {
      const next = new Map(map);
      next.set(`${ticketId}:${action.label}`, { status: 'confirming' });
      return next;
    });
  }

  onConfirmAction(action: EnrichedSocAction): void {
    if (!action.availableAction) return;
    this.responseActionsService.executeAction(this.ticket().request_id, action.availableAction).subscribe();
  }

  onCancelAction(action: EnrichedSocAction): void {
    this.responseActionsService.resetState(this.ticket().request_id, action.label);
  }

  onRequestSoc(action: EnrichedSocAction): void {
    this.responseActionsService.requestSoc(this.ticket().request_id, action.label, action.hostname).subscribe();
  }

  onRetry(action: EnrichedSocAction): void {
    this.responseActionsService.resetState(this.ticket().request_id, action.label);
  }
}
